import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardCheck, BellRing } from "lucide-react";

export default function ForecastApprovals() {
  const { t, isManager } = useApp();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    api.get(`/forecast-submissions`, { params: { status: "submitted" } }).then((r) => setSubs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const scopeOf = (s) => s.project_id ? s.project_id : "customer";

  const approve = async (s) => {
    try {
      await api.post(`/forecast-submissions/approve`, { account_id: s.account_id, year: s.year, project_id: scopeOf(s) });
      toast.success(t("approved_ok") + " ✓");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const doReject = async () => {
    try {
      await api.post(`/forecast-submissions/reject`, { account_id: rejectFor.account_id, year: rejectFor.year, project_id: scopeOf(rejectFor), comment });
      toast.success(t("rejected_ok"));
      setRejectFor(null); setComment(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const runReminders = async () => {
    try {
      const r = await api.post(`/forecast-reminders/run`);
      toast.success(`${t("reminders_sent_msg")}: ${r.data.reminders_sent}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!isManager) return <div className="text-gray-400" data-testid="forecast-approvals-page">—</div>;

  return (
    <div className="space-y-5" data-testid="forecast-approvals-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
          <ClipboardCheck className="h-7 w-7 text-[#F5A623]" />{t("forecast_approvals")}
        </h1>
        <Button variant="outline" size="sm" onClick={runReminders} data-testid="run-reminders-btn">
          <BellRing className="h-3.5 w-3.5 mr-1.5" />{t("send_reminders")}
        </Button>
      </div>
      {subs.length === 0 && <p className="text-gray-400 text-sm" data-testid="no-approvals">{t("no_pending_approvals")}</p>}
      <div className="space-y-3">
        {subs.map((s) => (
          <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="approval-row">
            <button onClick={() => navigate(`/accounts/${s.account_id}`)} className="text-left flex-1 min-w-[240px]">
              <p className="font-semibold text-gray-900">{s.account_name}</p>
              <p className="text-xs text-gray-500">{s.project_name || t("customer_level")} · {t("fiscal_year")} {s.year} · {t("submitted_by")} {s.submitted_by_name}</p>
            </button>
            <div className="text-right">
              <p className="text-xs text-gray-400">{t("total_forecast")}</p>
              <p className="font-mono font-semibold text-[#1B2A4A]">{fmtEur(s.fc_revenue)}</p>
            </div>
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{t("st_submitted")}</Badge>
            <Button size="sm" onClick={() => approve(s)} data-testid="approval-approve-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{t("fc_approve")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setRejectFor(s); setComment(""); }} data-testid="approval-reject-btn" className="text-red-600 border-red-200 hover:bg-red-50">
              <XCircle className="h-3.5 w-3.5 mr-1.5" />{t("fc_reject")}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("fc_reject")}</DialogTitle>
            <DialogDescription className="sr-only">{t("reject_reason")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("reject_reason")}
              rows={3} data-testid="approval-reject-comment" className="bg-white" />
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={doReject} data-testid="approval-confirm-reject-btn">{t("fc_reject")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
